import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTint, FaChartLine, FaCalendarAlt, FaCalendarWeek, FaLeaf, FaDrumstickBite, FaFish, FaHeartbeat, FaBone, FaAppleAlt } from 'react-icons/fa';
import { api } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import './NutritionDashboard.css';

function NutritionDashboard({ userId, profile, mealPlan }) {
  const [currentIntake, setCurrentIntake] = useState({
    iron: 0,
    protein: 0,
    folate: 0,
    calcium: 0,
    fiber: 0,
    omega3: 0
  });
  const [hydrationCups, setHydrationCups] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'weekly'
  const [selectedNutrients, setSelectedNutrients] = useState(new Set());
  
  const nutrientIcons = {
    iron: <FaHeartbeat />,
    protein: <FaDrumstickBite />,
    folate: <FaLeaf />,
    calcium: <FaBone />,
    fiber: <FaAppleAlt />,
    omega3: <FaFish />
  };
  
  const calculateIntakeFromMealPlan = useCallback(() => {
    if (!mealPlan?.days) return;
    
    const intake = {
      iron: 0,
      protein: 0,
      folate: 0,
      calcium: 0,
      fiber: 0,
      omega3: 0
    };
    
    const today = new Date().toISOString().split('T')[0];
    const todayMeals = mealPlan.days.find(day => {
      const dayDate = day.date ? (typeof day.date === 'string' ? day.date : day.date.toISOString().split('T')[0]) : null;
      return dayDate === today;
    });
    
    if (todayMeals) {
      ['breakfast', 'lunch', 'dinner', 'snack'].forEach(mealType => {
        const meal = todayMeals[mealType];
        if (meal) {
          if (meal.iron) intake.iron += meal.iron;
          if (meal.protein) intake.protein += meal.protein;
          if (meal.folate) intake.folate += meal.folate;
          if (meal.calcium) intake.calcium += meal.calcium;
          if (meal.fiber) intake.fiber += meal.fiber;
          if (meal.omega3) intake.omega3 += meal.omega3;
        }
      });
      
      if (todayMeals.hydrationActualCups) {
        setHydrationCups(todayMeals.hydrationActualCups);
      }
    }
    
    setCurrentIntake(intake);
    setLoading(false);
  }, [mealPlan]);
  
  const loadNutritionData = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const summary = await api.getSmartDailySummary(userId, false);
      
      if (summary?.data?.nutritionData) {
        const nutrition = summary.data.nutritionData;
        setCurrentIntake({
          iron: nutrition.iron || 0,
          protein: nutrition.protein || 0,
          folate: nutrition.folate || 0,
          calcium: nutrition.calcium || 0,
          fiber: nutrition.fiber || 0,
          omega3: nutrition.omega3 || 0
        });
      }
    } catch (error) {
      console.error('Error loading nutrition data:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId && mealPlan) {
      calculateIntakeFromMealPlan();
    } else if (userId) {
      loadNutritionData();
    }
  }, [userId, mealPlan, calculateIntakeFromMealPlan, loadNutritionData]);
  
  const getNutrientGoals = () => {
    if (!profile?.lmpDate) {
      return {
        iron: 27,
        protein: 70,
        folate: 600,
        calcium: 1000,
        fiber: 28,
        omega3: 200
      };
    }
    
    const lmpDate = new Date(profile.lmpDate);
    const today = new Date();
    const daysDiff = Math.floor((today - lmpDate) / (1000 * 60 * 60 * 24));
    const week = Math.floor(daysDiff / 7);
    const trimester = week < 13 ? 1 : week < 27 ? 2 : 3;
    
    return {
      iron: 27,
      protein: trimester === 1 ? 60 : trimester === 2 ? 70 : 75,
      folate: 600,
      calcium: 1000,
      fiber: trimester === 3 ? 28 : 25,
      omega3: 200
    };
  };
  
  const nutrientGoals = getNutrientGoals();
  
  const getProgressColor = (progress) => {
    if (progress >= 100) return { bg: '#4CAF50', text: '#2E7D32' };
    if (progress >= 80) return { bg: '#66BB6A', text: '#388E3C' };
    if (progress >= 50) return { bg: '#FFC107', text: '#F57C00' };
    if (progress >= 25) return { bg: '#FF9800', text: '#E65100' };
    return { bg: '#E91E63', text: '#C2185B' };
  };
  
  const getEncouragement = (nutrient, progress, current, goal) => {
    if (progress >= 100) {
      return { message: `Perfect! You've exceeded your ${nutrient} goal!`, emoji: '🎉', type: 'success' };
    }
    if (progress >= 80) {
      return { message: `Excellent! You're meeting your ${nutrient} needs!`, emoji: '💪', type: 'success' };
    }
    if (progress >= 50) {
      const remaining = goal - current;
      const unit = nutrient === 'protein' || nutrient === 'fiber' ? 'g' : 
                  nutrient === 'iron' || nutrient === 'calcium' ? 'mg' : 
                  nutrient === 'omega3' ? 'mg' : 'mcg';
      return { message: `You're close! Just ${remaining.toFixed(1)}${unit} more to meet your ${nutrient} goal!`, emoji: '✨', type: 'warning' };
    }
    if (progress >= 25) {
      return { message: `Good progress on ${nutrient}! You're at ${progress.toFixed(0)}% - keep adding ${nutrient}-rich foods!`, emoji: '💕', type: 'info' };
    }
    if (progress > 0) {
      return { message: `Every bit of ${nutrient} helps! You're at ${progress.toFixed(0)}% - try adding more ${nutrient}-rich foods today`, emoji: '🌱', type: 'info' };
    }
    return { message: `Start tracking your ${nutrient} intake! Add meals to see your progress`, emoji: '📊', type: 'info' };
  };
  
  const getNutrientInfo = (nutrient) => {
    const info = {
      iron: { name: 'Iron', description: 'Essential for red blood cell production and preventing anemia', foods: ['Lean red meat', 'Lentils', 'Spinach', 'Fortified cereals'] },
      protein: { name: 'Protein', description: 'Building blocks for your baby\'s growth and development', foods: ['Chicken', 'Eggs', 'Beans', 'Greek yogurt'] },
      folate: { name: 'Folate', description: 'Critical for preventing neural tube defects', foods: ['Dark leafy greens', 'Lentils', 'Fortified cereals', 'Avocado'] },
      calcium: { name: 'Calcium', description: 'Builds your baby\'s bones and teeth', foods: ['Dairy products', 'Dark leafy greens', 'Tofu', 'Fortified plant milks'] },
      fiber: { name: 'Fiber', description: 'Prevents constipation and supports digestive health', foods: ['Whole grains', 'Fruits', 'Vegetables', 'Legumes'] },
      omega3: { name: 'Omega-3', description: 'Supports baby\'s brain and eye development', foods: ['Salmon', 'Walnuts', 'Chia seeds', 'Flaxseeds'] }
    };
    return info[nutrient] || { name: nutrient, description: '', foods: [] };
  };
  
  if (loading) {
    return (
      <div className="nutrition-dashboard-modern">
        <LoadingSpinner message="Loading your nutrition data..." />
      </div>
    );
  }
  
  const totalProgress = Object.entries(nutrientGoals).reduce((sum, [nutrient, goal]) => {
    const current = currentIntake[nutrient] || 0;
    return sum + Math.min((current / goal) * 100, 100);
  }, 0) / Object.keys(nutrientGoals).length;
  
  return (
    <div className="nutrition-dashboard-modern">
      <div className="dashboard-header-modern">
        <motion.div
          className="dashboard-icon-wrapper"
          animate={{ rotate: [0, -10, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
        >
          <FaChartLine className="dashboard-icon" />
        </motion.div>
        <div className="dashboard-header-text">
          <h2>Nutrition Dashboard</h2>
          <p>Track your daily nutrient intake and stay on target with personalized goals based on your pregnancy stage</p>
          <div className="dashboard-help">
            <span className="help-icon">💡</span>
            <span className="help-text">Click on any nutrient card to see detailed information, food sources, and tips for meeting your goals</span>
          </div>
        </div>
      </div>
      
      <div className="overall-progress-card">
        <div className="overall-progress-header">
          <div className="overall-progress-title-section">
            <h3>Overall Nutrition Progress</h3>
            <p className="overall-progress-description">Combined progress across all essential nutrients for your pregnancy</p>
          </div>
          <div className="overall-percentage-wrapper">
            <span className="overall-percentage">{Math.round(totalProgress)}%</span>
            <span className="overall-percentage-label">Complete</span>
          </div>
        </div>
        <div className="overall-progress-bar">
          <motion.div
            className="overall-progress-fill"
            initial={{ width: 0 }}
            animate={{ width: `${totalProgress}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            style={{ background: totalProgress >= 80 ? 'linear-gradient(90deg, #4CAF50, #66BB6A)' : 
                              totalProgress >= 50 ? 'linear-gradient(90deg, #FFC107, #FFB300)' : 
                              'linear-gradient(90deg, #E91E63, #EC407A)' }}
          />
        </div>
        <div className="overall-message-section">
          <p className="overall-message">
            {totalProgress >= 80 ? '🎉 Excellent! You\'re meeting most of your nutrition goals!' :
             totalProgress >= 50 ? '✨ Good progress! Keep adding nutrient-rich foods!' :
             '💪 Every meal counts! Focus on adding more nutrient-dense foods!'}
          </p>
          <p className="overall-hint">
            {totalProgress >= 80 
              ? 'Keep up the great work! Your balanced nutrition is supporting your baby\'s healthy development.'
              : totalProgress >= 50
              ? 'You\'re on the right track! Try adding more nutrient-rich foods to reach your goals.'
              : 'Start by adding meals to your daily plan. Each meal contributes to your nutrition goals!'}
          </p>
        </div>
      </div>
      
      <div className="view-mode-section">
        <div className="view-mode-header">
          <h3>View Mode</h3>
          <span className="view-mode-hint">Switch between daily and weekly views to track your nutrition over time</span>
        </div>
        <div className="view-mode-toggle">
          <motion.button
            className={`view-btn ${viewMode === 'daily' ? 'active' : ''}`}
            onClick={() => setViewMode('daily')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="View today's nutrition intake and goals"
          >
            <FaCalendarAlt className="view-btn-icon" />
            <span>Daily View</span>
          </motion.button>
          <motion.button
            className={`view-btn ${viewMode === 'weekly' ? 'active' : ''}`}
            onClick={() => setViewMode('weekly')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="View weekly nutrition trends and averages"
          >
            <FaCalendarWeek className="view-btn-icon" />
            <span>Weekly View</span>
          </motion.button>
        </div>
      </div>
      
      <div className="nutrient-cards-modern">
        {Object.entries(nutrientGoals).map(([nutrient, goal], index) => {
          const current = currentIntake[nutrient] || 0;
          const progress = Math.min((current / goal) * 100, 100);
          const unit = nutrient === 'protein' || nutrient === 'fiber' ? 'g' : 
                      nutrient === 'iron' || nutrient === 'calcium' ? 'mg' : 
                      nutrient === 'omega3' ? 'mg' : 'mcg';
          const colors = getProgressColor(progress);
          const encouragement = getEncouragement(nutrient, progress, current, goal);
          const info = getNutrientInfo(nutrient);
          const isSelected = selectedNutrients.has(nutrient);
          
          return (
            <motion.div
              key={nutrient}
              className={`nutrient-card-modern ${isSelected ? 'expanded' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02, y: -4 }}
              onClick={(e) => {
                // Only toggle if clicking on the card itself, not on the button
                if (e.target.closest('.nutrient-expand-button')) {
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
                // Toggle the specific nutrient independently - allow multiple to be expanded
                setSelectedNutrients(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(nutrient)) {
                    newSet.delete(nutrient);
                  } else {
                    newSet.add(nutrient);
                  }
                  return newSet;
                });
              }}
            >
              <div className="nutrient-card-header-modern">
                <div className="nutrient-icon-name">
                  <div className="nutrient-icon-wrapper">
                    {nutrientIcons[nutrient]}
                  </div>
                  <div className="nutrient-text-content">
                    <h3>{info.name}</h3>
                    <p className="nutrient-description-short">{info.description}</p>
                    <p className="nutrient-goal-info">
                      Daily Goal: <strong>{goal}{unit}</strong>
                      {profile?.lmpDate && (() => {
                        const lmpDate = new Date(profile.lmpDate);
                        const today = new Date();
                        const daysDiff = Math.floor((today - lmpDate) / (1000 * 60 * 60 * 24));
                        const week = Math.floor(daysDiff / 7);
                        const trimester = week < 13 ? 1 : week < 27 ? 2 : 3;
                        return <span className="trimester-info"> • Based on {trimester === 1 ? '1st' : trimester === 2 ? '2nd' : '3rd'} trimester needs</span>;
                      })()}
                    </p>
                  </div>
                </div>
                <div className="nutrient-header-right">
                  <div className="nutrient-value-modern">
                    <div className="value-display">
                      <span className="current-value">{current.toFixed(1)}{unit}</span>
                      <span className="goal-value">/ {goal}{unit}</span>
                    </div>
                    <span className="value-hint">Click to expand</span>
                  </div>
                  <button
                    className="nutrient-expand-button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedNutrients(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has(nutrient)) {
                          newSet.delete(nutrient);
                        } else {
                          newSet.add(nutrient);
                        }
                        return newSet;
                      });
                    }}
                    aria-label={isSelected ? 'Collapse nutrient details' : 'Expand nutrient details'}
                  >
                    <span className={`nutrient-expand-icon ${isSelected ? 'expanded' : ''}`}>▼</span>
                  </button>
                </div>
              </div>
              
              <div className="nutrient-progress-container">
                <div className="nutrient-progress-bar-modern">
                  <motion.div
                    className="nutrient-progress-fill-modern"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                    style={{ 
                      background: `linear-gradient(90deg, ${colors.bg}, ${colors.text})`,
                      boxShadow: `0 2px 12px ${colors.bg}40`
                    }}
                  />
                </div>
                <span className="progress-percentage-badge" style={{ color: colors.text }}>
                  {Math.round(progress)}%
                </span>
              </div>
              
              <motion.div
                className={`nutrient-encouragement-modern ${encouragement.type}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 + index * 0.1 }}
              >
                <span className="encouragement-emoji">{encouragement.emoji}</span>
                <span className="encouragement-text">{encouragement.message}</span>
              </motion.div>
              
              <AnimatePresence>
                {isSelected && (
                  <motion.div
                    className="nutrient-details-expanded"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                  >
                    <div className="nutrient-info-section">
                      <h4>Why It Matters</h4>
                      <p>{info.description}</p>
                    </div>
                    <div className="nutrient-foods-section">
                      <h4>
                        <FaLeaf className="section-icon-small" />
                        Best Food Sources
                      </h4>
                      <p className="food-sources-intro">These foods are rich in {info.name.toLowerCase()} and perfect for pregnancy:</p>
                      <ul className="food-sources-list">
                        {info.foods.map((food, idx) => (
                          <li key={idx} className="food-source-item">
                            <span className="food-source-bullet"></span>
                            <span className="food-source-text">{food}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {progress < 80 && (
                      <div className="nutrient-tip-section">
                        <h4>
                          <FaChartLine className="section-icon-small" />
                          Quick Tip to Meet Your Goal
                        </h4>
                        <p className="tip-content">
                          <strong>Try this:</strong> Add <strong>{info.foods[0]}</strong> or <strong>{info.foods[1]}</strong> to your next meal. 
                          {nutrient === 'iron' && ' Pair with vitamin C-rich foods (like citrus) to enhance absorption!'}
                          {nutrient === 'calcium' && ' Spread calcium intake throughout the day for better absorption!'}
                          {nutrient === 'folate' && ' Start your day with folate-rich foods - it\'s most important in early pregnancy!'}
                          {nutrient === 'protein' && ' Include protein in every meal to support your baby\'s growth!'}
                          {nutrient === 'fiber' && ' Drink plenty of water with fiber-rich foods to prevent constipation!'}
                          {nutrient === 'omega3' && ' Choose low-mercury fish like salmon 2-3 times per week!'}
                        </p>
                      </div>
                    )}
                    {progress >= 80 && (
                      <div className="nutrient-success-section">
                        <h4>
                          <FaChartLine className="section-icon-small" />
                          You're Doing Great!
                        </h4>
                        <p>You're meeting your {info.name.toLowerCase()} needs! Keep up the excellent work by maintaining a balanced diet.</p>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
      
      <motion.div
        className="hydration-section-modern"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <div className="hydration-header">
          <div className="hydration-header-main">
            <h3>
              <FaTint className="hydration-icon-header" />
              Hydration Tracker
            </h3>
            <p className="hydration-description">Stay hydrated! Water is essential for your increased blood volume and helps prevent common pregnancy issues like constipation and swelling.</p>
          </div>
          <div className="hydration-count-wrapper">
            <span className="hydration-count">{hydrationCups} / 8 cups</span>
            <span className="hydration-goal-hint">Daily Goal</span>
          </div>
        </div>
        
        <div className="hydration-visual">
          <div className="hydration-cups-grid">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((cup) => {
              const isFilled = cup <= hydrationCups;
              return (
                <motion.div
                  key={cup}
                  className={`hydration-cup ${isFilled ? 'filled' : ''}`}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.7 + cup * 0.05 }}
                  whileHover={{ scale: 1.1 }}
                  title={`Cup ${cup} of 8`}
                >
                  <FaTint className="hydration-cup-icon" />
                </motion.div>
              );
            })}
          </div>
        </div>
        
        <div className="hydration-encouragement-modern">
          {hydrationCups >= 8 ? (
            <p className="hydration-message success">
              <span className="message-icon">✨</span>
              Excellent! You've met your hydration goal today!
            </p>
          ) : hydrationCups >= 6 ? (
            <p className="hydration-message warning">
              <span className="message-icon">💪</span>
              Almost there! Just {8 - hydrationCups} more cup{8 - hydrationCups > 1 ? 's' : ''} to go!
            </p>
          ) : hydrationCups >= 4 ? (
            <p className="hydration-message info">
              <span className="message-icon">💕</span>
              You're doing great! Keep hydrating - {8 - hydrationCups} more cups to reach your goal
            </p>
          ) : hydrationCups >= 2 ? (
            <p className="hydration-message info">
              <span className="message-icon">💧</span>
              Good start! Remember to drink water throughout the day - {8 - hydrationCups} more cups needed
            </p>
          ) : hydrationCups >= 1 ? (
            <p className="hydration-message info">
              <span className="message-icon">🌊</span>
              Every cup counts! Try to drink {8 - hydrationCups} more cups today
            </p>
          ) : (
            <p className="hydration-message info">
              <span className="message-icon">💧</span>
              Start your day with hydration! Aim for 8 cups of water today
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default NutritionDashboard;
